import { Box, Button, chakra, Field, Flex, Input, type BoxProps } from '@chakra-ui/react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { useState } from 'react';
import { OptionsArray } from '@halo/utils';
import type { CoinState } from '@halo/wallet-app';
import { BIF, EVMGasPaymentMethod } from '@halo/wallet-app';
import {
  EVMGasLevels,
  type EVMGasLevel,
  type EVMTransactionFeeResult,
  formatAmount,
} from '@halo/wallet-app';
import { useTranslation } from 'react-i18next';
import { useWalletStore } from '@/wallet';
import { DrawerPanel } from './drawer-panel';
import BottomIcon from './images/bottom.svg?react';
import TopIcon from './images/top.svg?react';
import IconSlow from './images/slow.svg?react';
import IconMedium from './images/middle.svg?react';
import IconFast from './images/fast.svg?react';

const evmGasLevelMetas = [
  {
    key: EVMGasLevels.LOW,
    labelKey: 'transfer.gasLevelLow',
    icon: <IconSlow></IconSlow>,
  },
  {
    key: EVMGasLevels.MEDIUM,
    labelKey: 'transfer.gasLevelMedium',
    icon: <IconMedium></IconMedium>,
  },
  {
    key: EVMGasLevels.HIGH,
    labelKey: 'transfer.gasLevelHigh',
    icon: <IconFast></IconFast>,
  },
  {
    key: EVMGasLevels.CUSTOM,
    labelKey: 'transfer.gasLevelCustom',
    icon: <IconSlow></IconSlow>,
  },
] as const;

export interface EVMGasLevelValues extends EVMGasLevel {
  customGasPrice: string;
  customMaxFeePerGas: string;
  customMaxPriorityFeePerGas: string;
}

export interface EvmGasSettingInfo {
  level: EVMGasLevels;
  info: EVMGasLevel;
}

export interface GasSettingsPanelProps {
  paymentMethod: EVMGasPaymentMethod | undefined;
  gasLevelInfo: EvmGasSettingInfo | undefined;
  onChangeGasLevelInfo: (gasLevelInfo: EvmGasSettingInfo) => void;
  triggerContent?: React.ReactNode;
  gasInfo: EVMTransactionFeeResult | undefined;
  mainCoin: CoinState | undefined;
  container?: React.RefObject<HTMLDivElement | null>;
}

export function GasSettingsPanel({
  paymentMethod,
  gasInfo,
  triggerContent,
  mainCoin,
  gasLevelInfo,
  onChangeGasLevelInfo,
  container,
}: GasSettingsPanelProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedGasLevel, setSelectedGasLevel] = useState<EVMGasLevels>(EVMGasLevels.MEDIUM);
  const form = useForm<EVMGasLevelValues>();
  const { curFiat, getCurrencyPrice } = useWalletStore();
  const [loading, setLoading] = useState(false);

  const evmGasLevels = useMemo(() => {
    return new OptionsArray(
      evmGasLevelMetas.map((item) => ({
        key: item.key,
        label: t(item.labelKey),
        icon: item.icon,
      }))
    );
  }, [t]);

  const isEIP1559 = paymentMethod === EVMGasPaymentMethod.EIP1559;

  const onConfirm = useCallback(async () => {
    if (!gasInfo || !mainCoin) {
      return;
    }

    setLoading(true);

    try {
      let gasInfo_: EVMGasLevel | undefined;

      if (selectedGasLevel === EVMGasLevels.CUSTOM) {
        await form.trigger();
        if (form.formState.isValid) {
          const values = form.getValues();

          gasInfo_ = values;

          const gasFee = (
            isEIP1559
              ? BIF.from(gasInfo.estimatedBaseFee)
                  .shiftLeft(9)
                  .sum(values.customMaxPriorityFeePerGas)
              : BIF.from(values.customGasPrice)
          )
            .shiftRight(9)
            .mul(values.gasLimit)
            .shiftLeft(mainCoin.decimal)
            .toString();

          gasInfo_.gasFee = gasFee;
          gasInfo_.currencyGasFee = await getCurrencyPrice(mainCoin, curFiat.symbol, gasFee);
        }
      } else {
        gasInfo_ = gasInfo[selectedGasLevel];
      }

      if (!gasInfo_) return;

      onChangeGasLevelInfo({
        level: selectedGasLevel,
        info: gasInfo_,
      });

      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  }, [
    curFiat.symbol,
    getCurrencyPrice,
    form,
    gasInfo,
    isEIP1559,
    mainCoin,
    onChangeGasLevelInfo,
    selectedGasLevel,
  ]);

  const onConfirmRef = useRef(onConfirm);

  onConfirmRef.current = onConfirm;

  useLayoutEffect(() => {
    setSelectedGasLevel(EVMGasLevels.MEDIUM);
  }, [paymentMethod]);

  useLayoutEffect(() => {
    if (gasInfo) {
      onConfirmRef.current();
      const info = gasInfo[EVMGasLevels.MEDIUM];
      if (info) {
        const map = {
          customGasPrice: 'gasPrice',
          customMaxFeePerGas: 'maxFeePerGas',
          customMaxPriorityFeePerGas: 'maxPriorityFeePerGas',
        };
        for (const key in map) {
          form.setValue(
            key as keyof EVMGasLevelValues,
            BIF.from(info[map[key as keyof typeof map] as keyof EVMGasLevel] || '0')
              .shiftLeft(9)
              .toString()
          );
        }
      }
    }
  }, [gasInfo, form]);

  useLayoutEffect(() => {
    if (isOpen && gasLevelInfo) {
      setSelectedGasLevel(gasLevelInfo.level);

      for (const key in gasLevelInfo.info) {
        form.setValue(key as keyof EVMGasLevel, gasLevelInfo.info[key as keyof EVMGasLevel]);
      }
    }
  }, [form, gasLevelInfo, isOpen]);

  return (
    <DrawerPanel
      container={container}
      triggerContent={triggerContent}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={
        <Flex
          flex="1"
          alignItems="center"
          justifyContent="center"
          fontSize="md"
          fontWeight="bold"
          ml="-20px"
        >
          {t('transfer.gasSettings')}
        </Flex>
      }
    >
      <Box>
        <Flex direction="column" gap="2.5">
          {evmGasLevels.map((item) => {
            const activated = selectedGasLevel === item.key;

            const onClick = () => {
              setSelectedGasLevel(item.key);
            };

            if (item.key === EVMGasLevels.CUSTOM) {
              return (
                <CustomGasItem
                  key={item.key}
                  mainCoin={mainCoin}
                  paymentMethod={paymentMethod}
                  baseFee={gasInfo?.estimatedBaseFee}
                  icon={item.icon}
                  label={item.label}
                  form={form}
                  activated={activated}
                  onClick={onClick}
                />
              );
            }

            return (
              <GasItem
                key={item.key}
                icon={item.icon}
                label={item.label}
                level={gasInfo?.[item.key]}
                mainCoin={mainCoin}
                activated={activated}
                onClick={onClick}
              />
            );
          })}
        </Flex>
        <Button loading={loading} onClick={onConfirm} size="xl" borderRadius="xl" w="full" my="9">
          {t('buttons.confirm')}
        </Button>
      </Box>
    </DrawerPanel>
  );
}

interface GasItemLayoutProps extends BoxProps {
  icon: ReactNode;
  label: ReactNode;
  activated?: boolean;
  description?: ReactNode;
  end?: ReactNode;
}

function GasItemLayout({
  icon,
  label,
  description,
  end,
  activated,
  children,
  ...props
}: GasItemLayoutProps) {
  return (
    <Box
      gap="2.5"
      py="3.5"
      px="3"
      bg="white"
      borderRadius="xl"
      bgColor={activated ? 'bg.emphasized' : 'white'}
      {...props}
    >
      <Flex alignItems="center" cursor="pointer">
        <Box w="36px" h="36px" borderRadius="md" flex="0 0 auto">
          {icon}
        </Box>
        <Box flex="1 1 auto" minW="0">
          <Box fontSize="sm" fontWeight="semibold">
            {label}
          </Box>
          {description && (
            <Box fontSize="xs" fontWeight="medium" color="fg.subtle" mt="1">
              {description}
            </Box>
          )}
        </Box>
        {end && <Box flex="0 0 auto">{end}</Box>}
      </Flex>
      {children}
    </Box>
  );
}

interface GasItemProps extends GasItemLayoutProps {
  level: EVMGasLevel | undefined;
  mainCoin: CoinState | undefined;
}

function GasItem({ icon, label, level, mainCoin, ...props }: GasItemProps) {
  const { coinAmountDisplay, currencyAmountDisplay } = useWalletStore();

  return (
    <GasItemLayout
      icon={icon}
      label={label}
      description={level?.estimatedTime}
      end={
        <Box>
          <Box fontSize="sm" fontWeight="semibold">
            {coinAmountDisplay(level?.gasFee, mainCoin?.symbol)}
          </Box>
          <Box fontSize="xs" fontWeight="medium" color="fg.subtle">
            {currencyAmountDisplay(level?.currencyGasFee)}
          </Box>
        </Box>
      }
      {...props}
    ></GasItemLayout>
  );
}

interface CustomGasItemProps extends GasItemLayoutProps {
  icon: ReactNode;
  label: ReactNode;
  form: UseFormReturn<EVMGasLevelValues>;
  paymentMethod: EVMGasPaymentMethod | undefined;
  baseFee: string | undefined;
  mainCoin: CoinState | undefined;
}

function CustomGasItem({
  icon,
  label,
  activated,
  form,
  paymentMethod,
  baseFee,
  mainCoin,
  ...props
}: CustomGasItemProps) {
  const {
    register,
    unregister,
    formState: { errors },
  } = form;

  const isEIP1559 = paymentMethod === EVMGasPaymentMethod.EIP1559;

  const baseFeeGWei = useMemo(
    () =>
      formatAmount(
        BIF.from(baseFee || '0')
          .shiftLeft(9)
          .toString()
      ),
    [baseFee]
  );

  useEffect(() => {
    if (isEIP1559) {
      unregister('customGasPrice');
    } else {
      unregister('customMaxFeePerGas');
      unregister('customMaxPriorityFeePerGas');
    }
  }, [isEIP1559, unregister]);

  return (
    <GasItemLayout
      icon={icon}
      label={label}
      end={<Box cursor="pointer">{activated ? <TopIcon /> : <BottomIcon />}</Box>}
      activated={activated}
      {...props}
    >
      {activated && (
        <Box bg="white" mx="-2" mb="-2" borderRadius="xl" p="2">
          {isEIP1559 ? (
            <Box>
              <Field.Root invalid={!!errors.customMaxFeePerGas}>
                <Field.Label fontSize="xs" fontWeight="medium">
                  Max Fee(Gwei)
                </Field.Label>
                <Input variant="subtle" {...register('customMaxFeePerGas', { required: true })} />
                <Field.ErrorText>{errors.customMaxFeePerGas?.message}</Field.ErrorText>
              </Field.Root>

              <Box fontSize="xs" fontWeight="medium" color="fg.subtle" pt="1.5">
                The current base fee is{' '}
                <chakra.span color="red.500">{formatAmount(baseFeeGWei)} Gwei</chakra.span>. A
                value lower than this will extend the transaction time.
              </Box>
            </Box>
          ) : (
            <Box>
              <Field.Root invalid={!!errors.customGasPrice}>
                <Field.Label fontSize="xs" fontWeight="medium">
                  Gas Price(Gwei)
                </Field.Label>
                <Input variant="subtle" {...register('customGasPrice', { required: true })} />
                <Field.ErrorText>{errors.customGasPrice?.message}</Field.ErrorText>
              </Field.Root>
            </Box>
          )}

          <Flex gap="3.5" mt="3.5">
            {isEIP1559 && (
              <Field.Root flex="1" invalid={!!errors.customMaxPriorityFeePerGas}>
                <Field.Label fontSize="xs" fontWeight="medium">
                  Priority Fee(Gwei)
                </Field.Label>
                <Input
                  variant="subtle"
                  {...register('customMaxPriorityFeePerGas', { required: true })}
                />
                <Field.ErrorText>{errors.customMaxPriorityFeePerGas?.message}</Field.ErrorText>
              </Field.Root>
            )}
            <Field.Root flex="1" invalid={!!errors.gasLimit}>
              <Field.Label fontSize="xs" fontWeight="medium">
                Gas Limit
              </Field.Label>
              <Input variant="subtle" {...register('gasLimit', { required: true })} />
              <Field.ErrorText>{errors.gasLimit?.message}</Field.ErrorText>
            </Field.Root>
          </Flex>
        </Box>
      )}
    </GasItemLayout>
  );
}
